import { useEffect, useRef, useState } from 'react'
import { ApiClientError } from '../../services/apiClient'
import {
  configClient,
  ALLOWED_IMAGE_TYPES,
  LOGO_MAX_BYTES,
  type TenantConfig,
  type ConfigUpdatePayload,
} from '../../services/configClient'
import { compressUploadImage } from '../../services/imageProcessing'
import {
  empresaClient,
  type EmpresaDatos,
  type TeamMember,
} from '../../services/empresaClient'
import { ProtectedImage } from '../../components/ProtectedImage'

type Tab = 'empresa' | 'configuracion' | 'equipo'

/**
 * Sección Empresa — visible solo para role='owner'.
 *
 * Tres pestañas:
 *   1. Empresa:       Nombre comercial, Razón Social, RFC, Código Postal, Logotipo.
 *   2. Configuración: Folio (prefix, sequence, page_size) + Opciones del PDF.
 *   3. Equipo:        Lista de miembros, invitar técnico, eliminar miembro.
 */
export function EmpresaPage() {
  const [tab, setTab] = useState<Tab>('empresa')
  const [empresa, setEmpresa] = useState<EmpresaDatos | null>(null)
  const [config, setConfig] = useState<TenantConfig | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([
      empresaClient.show(),
      configClient.show(),
    ])
      .then(([eRes, cRes]) => {
        setEmpresa(eRes.empresa)
        setConfig(cRes.config)
      })
      .catch((err) =>
        setLoadError(
          err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar la información.',
        ),
      )
  }, [])

  if (loadError) {
    return (
      <div className="content-stack">
        <h1>Empresa</h1>
        <p className="error">{loadError}</p>
      </div>
    )
  }

  if (!empresa || !config) {
    return (
      <div className="content-stack">
        <h1>Empresa</h1>
        <p className="muted">Cargando…</p>
      </div>
    )
  }

  return (
    <div className="content-stack">
      <header className="list-toolbar">
        <div className="list-toolbar__title">
          <h1>Empresa</h1>
          <span className="muted">{empresa.company_name}</span>
        </div>
      </header>

      <div className="tabs">
        <button
          type="button"
          className={`tab-button${tab === 'empresa' ? ' active' : ''}`}
          onClick={() => setTab('empresa')}
        >
          Datos de la empresa
        </button>
        <button
          type="button"
          className={`tab-button${tab === 'configuracion' ? ' active' : ''}`}
          onClick={() => setTab('configuracion')}
        >
          Configuración
        </button>
        <button
          type="button"
          className={`tab-button${tab === 'equipo' ? ' active' : ''}`}
          onClick={() => setTab('equipo')}
        >
          Equipo de trabajo
        </button>
      </div>

      {tab === 'empresa' && (
        <>
          <EmpresaDatosSection empresa={empresa} onSaved={setEmpresa} />
          <LogoSection config={config} onSaved={setConfig} />
        </>
      )}
      {tab === 'configuracion' && (
        <>
          <FolioSection config={config} onSaved={setConfig} />
          <PdfTogglesSection config={config} onSaved={setConfig} />
        </>
      )}
      {tab === 'equipo' && (
        <EquipoSection />
      )}
    </div>
  )
}

// ─── Tab 1: Datos de la empresa ──────────────────────────────────────────────

function EmpresaDatosSection({
  empresa,
  onSaved,
}: {
  empresa: EmpresaDatos
  onSaved: (e: EmpresaDatos) => void
}) {
  const [companyName, setCompanyName] = useState(empresa.company_name)
  const [legalName, setLegalName] = useState(empresa.legal_name ?? '')
  const [rfc, setRfc] = useState(empresa.rfc ?? '')
  const [postalCode, setPostalCode] = useState(empresa.postal_code ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMsg, setSavedMsg] = useState<string | null>(null)

  const dirty =
    companyName !== empresa.company_name ||
    legalName !== (empresa.legal_name ?? '') ||
    rfc !== (empresa.rfc ?? '') ||
    postalCode !== (empresa.postal_code ?? '')

  async function handleSave() {
    setError(null)
    setSavedMsg(null)
    setSaving(true)
    try {
      const res = await empresaClient.updateDatos({
        company_name: companyName.trim(),
        legal_name: legalName.trim() || null,
        rfc: rfc.trim() || null,
        postal_code: postalCode.trim() || null,
      })
      onSaved(res.empresa)
      setSavedMsg('Guardado.')
      setTimeout(() => setSavedMsg(null), 2500)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card">
      <h3>Datos de la empresa</h3>
      <div className="form-grid">
        <label className="field">
          <span>Nombre Comercial</span>
          <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} maxLength={255} disabled={saving} />
        </label>
        <label className="field">
          <span>Razón Social</span>
          <input value={legalName} onChange={(e) => setLegalName(e.target.value)} maxLength={255} disabled={saving} />
        </label>
        <div className="field-row">
          <label className="field">
            <span>RFC</span>
            <input value={rfc} onChange={(e) => setRfc(e.target.value.toUpperCase())} maxLength={15} className="mono" disabled={saving} />
          </label>
          <label className="field">
            <span>Código Postal</span>
            <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} maxLength={10} className="mono" disabled={saving} />
          </label>
        </div>
        {error && <p className="error">{error}</p>}
        {savedMsg && <p className="info">{savedMsg}</p>}
        <div className="modal__actions">
          <button type="button" className="primary-btn" onClick={handleSave} disabled={saving || !dirty}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </section>
  )
}

function LogoSection({ config, onSaved }: { config: TenantConfig; onSaved: (c: TenantConfig) => void }) {
  return (
    <section className="card">
      <h3>Logotipo de la empresa</h3>
      <div className="image-config-grid">
        <ImageUploader
          label="Logotipo"
          help="Se mostrará en el encabezado del PDF. PNG, JPG o WEBP. Máx. 2 MB."
          path={config.logo_path}
          maxBytes={LOGO_MAX_BYTES}
          onUpload={async (file) => {
            const res = await configClient.uploadLogo(file)
            onSaved(res.config)
          }}
          onDelete={async () => {
            const res = await configClient.deleteLogo()
            onSaved(res.config)
          }}
        />
      </div>
    </section>
  )
}

// ─── Tab 2: Configuración (folio + PDF toggles) ──────────────────────────────

function FolioSection({ config, onSaved }: { config: TenantConfig; onSaved: (c: TenantConfig) => void }) {
  const [folioPrefix, setFolioPrefix] = useState(config.folio_prefix)
  const [currentSequence, setCurrentSequence] = useState(String(config.current_sequence))
  const [pageSize, setPageSize] = useState(config.page_size)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMsg, setSavedMsg] = useState<string | null>(null)

  const seqNum = Number.parseInt(currentSequence, 10)
  const nextSeq = Number.isFinite(seqNum) ? seqNum + 1 : config.current_sequence + 1
  const folioPreview = `${folioPrefix}${String(Math.max(0, nextSeq)).padStart(5, '0')}`

  const dirty =
    folioPrefix !== config.folio_prefix ||
    Number.parseInt(currentSequence, 10) !== config.current_sequence ||
    pageSize !== config.page_size

  async function handleSave() {
    setError(null)
    setSavedMsg(null)
    setSaving(true)
    try {
      const payload: ConfigUpdatePayload = {
        folio_prefix: folioPrefix,
        current_sequence: Number.parseInt(currentSequence, 10),
        page_size: pageSize,
      }
      const res = await configClient.update(payload)
      onSaved(res.config)
      setSavedMsg('Guardado.')
      setTimeout(() => setSavedMsg(null), 2500)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card">
      <h3>Folio</h3>
      <div className="form-grid">
        <div className="field-row">
          <label className="field">
            <span>Prefijo de folio <em className="muted">(máx. 10 caracteres)</em></span>
            <input value={folioPrefix} onChange={(e) => setFolioPrefix(e.target.value)} maxLength={10} placeholder="DEMO-" className="mono" disabled={saving} />
          </label>
          <label className="field">
            <span>Consecutivo actual <em className="muted">(último folio emitido)</em></span>
            <input type="number" value={currentSequence} onChange={(e) => setCurrentSequence(e.target.value)} min={0} disabled={saving} className="mono" />
          </label>
        </div>
        <div className="config-preview">
          <small className="muted">Siguiente folio</small>
          <div className="mono config-preview__value">{folioPreview}</div>
        </div>
        <label className="field">
          <span>Tamaño de hoja del PDF</span>
          <select value={pageSize} onChange={(e) => setPageSize(e.target.value as 'LETTER' | 'A4')} disabled={saving}>
            <option value="LETTER">Carta (Letter)</option>
            <option value="A4">A4</option>
          </select>
        </label>
        {error && <p className="error">{error}</p>}
        {savedMsg && <p className="info">{savedMsg}</p>}
        <div className="modal__actions">
          <button type="button" className="primary-btn" onClick={handleSave} disabled={saving || !dirty}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </section>
  )
}

function PdfTogglesSection({ config, onSaved }: { config: TenantConfig; onSaved: (c: TenantConfig) => void }) {
  const [signatureEnabled, setSignatureEnabled] = useState(config.signature_enabled)
  const [clientSignatureEnabled, setClientSignatureEnabled] = useState(config.client_signature_enabled)
  const [responsibleEnabled, setResponsibleEnabled] = useState(config.responsible_name_enabled)
  const [showCompanyName, setShowCompanyName] = useState(config.show_company_name)
  const [showFolio, setShowFolio] = useState(config.show_folio)
  const [showClient, setShowClient] = useState(config.show_client)
  const [showRfc, setShowRfc] = useState(config.show_rfc)
  const [showAddress, setShowAddress] = useState(config.show_address)
  const [showSystem, setShowSystem] = useState(config.show_system)
  const [showBuilding, setShowBuilding] = useState(config.show_building)
  const [showOrder, setShowOrder] = useState(config.show_order)
  const [showCity, setShowCity] = useState(config.show_city)
  const [showDates, setShowDates] = useState(config.show_dates)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMsg, setSavedMsg] = useState<string | null>(null)

  const dirty =
    signatureEnabled !== config.signature_enabled ||
    clientSignatureEnabled !== config.client_signature_enabled ||
    responsibleEnabled !== config.responsible_name_enabled ||
    showCompanyName !== config.show_company_name ||
    showFolio !== config.show_folio ||
    showClient !== config.show_client ||
    showRfc !== config.show_rfc ||
    showAddress !== config.show_address ||
    showSystem !== config.show_system ||
    showBuilding !== config.show_building ||
    showOrder !== config.show_order ||
    showCity !== config.show_city ||
    showDates !== config.show_dates

  async function handleSave() {
    setError(null)
    setSavedMsg(null)
    setSaving(true)
    try {
      const res = await configClient.update({
        signature_enabled: signatureEnabled,
        client_signature_enabled: clientSignatureEnabled,
        responsible_name_enabled: responsibleEnabled,
        show_company_name: showCompanyName,
        show_folio: showFolio,
        show_client: showClient,
        show_rfc: showRfc,
        show_address: showAddress,
        show_system: showSystem,
        show_building: showBuilding,
        show_order: showOrder,
        show_city: showCity,
        show_dates: showDates,
      })
      onSaved(res.config)
      setSavedMsg('Guardado.')
      setTimeout(() => setSavedMsg(null), 2500)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo guardar.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card">
      <h3>Opciones del PDF</h3>
      <div className="form-grid">
        <p className="field-group-label muted"><strong>Firma</strong></p>
        <ToggleRow label="Incluir firma del responsable en el PDF" help="Solo se incluye si el técnico tiene imagen de firma en su perfil." checked={signatureEnabled} onChange={setSignatureEnabled} disabled={saving} />
        <ToggleRow label="Imprimir nombre del responsable" help="Aparece bajo la sección de firma." checked={responsibleEnabled} onChange={setResponsibleEnabled} disabled={saving} />
        <ToggleRow label="Solicitar firma del cliente al cerrar reporte" help="Al cerrar un reporte aparecerá un modal para dibujar la firma del cliente, que se incluirá en el PDF." checked={clientSignatureEnabled} onChange={setClientSignatureEnabled} disabled={saving} />

        <p className="field-group-label muted"><strong>Encabezados</strong></p>
        <ToggleRow label="Nombre de la empresa" checked={showCompanyName} onChange={setShowCompanyName} disabled={saving} />
        <ToggleRow label="Folio" checked={showFolio} onChange={setShowFolio} disabled={saving} />

        <p className="field-group-label muted"><strong>Datos básicos del reporte</strong></p>
        <ToggleRow label="Cliente" checked={showClient} onChange={setShowClient} disabled={saving} />
        <ToggleRow label="RFC" checked={showRfc} onChange={setShowRfc} disabled={saving} />
        <ToggleRow label="Domicilio" checked={showAddress} onChange={setShowAddress} disabled={saving} />
        <ToggleRow label="Sistema" checked={showSystem} onChange={setShowSystem} disabled={saving} />
        <ToggleRow label="Edificio / Instalación" checked={showBuilding} onChange={setShowBuilding} disabled={saving} />
        <ToggleRow label="Orden" checked={showOrder} onChange={setShowOrder} disabled={saving} />
        <ToggleRow label="Ciudad" checked={showCity} onChange={setShowCity} disabled={saving} />
        <ToggleRow label="Fecha de Inicio y Fecha Fin" checked={showDates} onChange={setShowDates} disabled={saving} />

        {error && <p className="error">{error}</p>}
        {savedMsg && <p className="info">{savedMsg}</p>}
        <div className="modal__actions">
          <button type="button" className="primary-btn" onClick={handleSave} disabled={saving || !dirty}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </section>
  )
}

// ─── Tab 3: Equipo de trabajo ────────────────────────────────────────────────

function EquipoSection() {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [maxUsers, setMaxUsers] = useState(-1)
  const [currentUsers, setCurrentUsers] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [removingId, setRemovingId] = useState<number | null>(null)
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null)

  function load() {
    setLoading(true)
    empresaClient
      .getEquipo()
      .then((res) => {
        setMembers(res.items)
        setMaxUsers(res.max_users)
        setCurrentUsers(res.current_users)
      })
      .catch((err) =>
        setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo cargar el equipo.'),
      )
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  async function handleRemove(userId: number) {
    setRemovingId(userId)
    try {
      await empresaClient.removeMember(userId)
      load()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo eliminar al miembro.')
    } finally {
      setRemovingId(null)
      setConfirmRemoveId(null)
    }
  }

  const usersLabel = maxUsers === -1
    ? `${currentUsers} usuario${currentUsers !== 1 ? 's' : ''}`
    : `${currentUsers} de ${maxUsers} usuario${maxUsers !== 1 ? 's' : ''}`

  if (loading) return <section className="card"><p className="muted">Cargando equipo…</p></section>

  return (
    <section className="card">
      <div className="card-header-row">
        <div>
          <h3>Equipo de trabajo</h3>
          <small className="muted">{usersLabel}</small>
        </div>
        <button type="button" className="primary-btn" onClick={() => setShowInviteModal(true)}>
          + Agregar técnico
        </button>
      </div>

      {error && <p className="error">{error}</p>}

      <table className="data-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Rol</th>
            <th>Correo</th>
            <th>Último acceso</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id}>
              <td>{m.first_name} {m.last_name_paternal}</td>
              <td>
                <span className={`role-badge role-badge--${m.role}`}>
                  {m.role === 'owner' ? 'Propietario' : 'Técnico'}
                </span>
              </td>
              <td>{m.email}</td>
              <td className="muted">{m.last_login_at ? new Date(m.last_login_at).toLocaleDateString('es-MX') : 'Nunca'}</td>
              <td>
                {m.role !== 'owner' && (
                  confirmRemoveId === m.id ? (
                    <span className="inline-confirm">
                      <span className="muted">¿Confirmar?</span>
                      <button
                        type="button"
                        className="danger-btn"
                        onClick={() => handleRemove(m.id)}
                        disabled={removingId === m.id}
                      >
                        {removingId === m.id ? 'Eliminando…' : 'Sí, eliminar'}
                      </button>
                      <button type="button" className="ghost-btn" onClick={() => setConfirmRemoveId(null)}>
                        Cancelar
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() => setConfirmRemoveId(m.id)}
                      disabled={removingId !== null}
                    >
                      Quitar
                    </button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showInviteModal && (
        <InviteModal
          onClose={() => setShowInviteModal(false)}
          onInvited={() => { setShowInviteModal(false); load() }}
        />
      )}
    </section>
  )
}

function InviteModal({ onClose, onInvited }: { onClose: () => void; onInvited: () => void }) {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastNamePaternal, setLastNamePaternal] = useState('')
  const [lastNameMaternal, setLastNameMaternal] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleInvite() {
    if (!email.trim()) {
      setError('El correo es requerido.')
      return
    }
    setError(null)
    setSaving(true)
    try {
      await empresaClient.inviteMember({
        email: email.trim(),
        first_name: firstName.trim() || undefined,
        last_name_paternal: lastNamePaternal.trim() || undefined,
        last_name_maternal: lastNameMaternal.trim() || undefined,
      })
      onInvited()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo enviar la invitación.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--md" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ padding: '1.25rem 1.25rem 0' }}>Agregar técnico</h2>
        <p className="muted" style={{ padding: '0.25rem 1.25rem 0' }}>Se enviará un correo con una contraseña temporal. El técnico deberá cambiarla al primer inicio de sesión.</p>

        <div className="form-grid" style={{ padding: '1.25rem' }}>
          <label className="field">
            <span>Correo electrónico <strong>*</strong></span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={saving} autoFocus />
          </label>
          <label className="field">
            <span>Nombre(s)</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={100} disabled={saving} />
          </label>
          <div className="field-row">
            <label className="field">
              <span>Apellido paterno</span>
              <input value={lastNamePaternal} onChange={(e) => setLastNamePaternal(e.target.value)} maxLength={100} disabled={saving} />
            </label>
            <label className="field">
              <span>Apellido materno</span>
              <input value={lastNameMaternal} onChange={(e) => setLastNameMaternal(e.target.value)} maxLength={100} disabled={saving} />
            </label>
          </div>
          {error && <p className="error">{error}</p>}
          <div className="modal__actions">
            <button type="button" className="ghost-btn" onClick={onClose} disabled={saving}>Cancelar</button>
            <button type="button" className="primary-btn" onClick={handleInvite} disabled={saving}>
              {saving ? 'Enviando…' : 'Enviar invitación'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Helpers UI ───────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  help,
  checked,
  onChange,
  disabled,
}: {
  label: string
  help?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className="toggle-row">
      <div className="toggle-row__text">
        <strong>{label}</strong>
        {help && <small className="muted">{help}</small>}
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="toggle-switch"
      />
    </label>
  )
}

function ImageUploader({
  label,
  help,
  path,
  maxBytes,
  onUpload,
  onDelete,
}: {
  label: string
  help: string
  path: string | null
  maxBytes: number
  onUpload: (file: File) => Promise<void>
  onDelete: () => Promise<void>
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleFile(file: File) {
    setError(null)
    if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
      setError('Formato no permitido. Usa PNG, JPG o WEBP.')
      return
    }
    setUploading(true)
    try {
      const processed = await compressUploadImage(file, maxBytes / 1024 / 1024)
      if (processed.size > maxBytes) {
        setError(`El archivo excede el tamaño máximo (${Math.ceil(maxBytes / 1024 / 1024)} MB).`)
        return
      }
      await onUpload(processed)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo subir el archivo.')
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function handleDelete() {
    setError(null)
    setDeleting(true)
    try {
      await onDelete()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.apiError.message : 'No se pudo eliminar.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="image-uploader">
      <div className="image-uploader__header">
        <strong>{label}</strong>
        <small className="muted">{help}</small>
      </div>
      <div className="image-uploader__preview">
        {path ? (
          <ProtectedImage path={path} alt={label} className="image-uploader__img" />
        ) : (
          <span className="muted">Sin imagen cargada</span>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      <div className="image-uploader__actions">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
        />
        <button type="button" className="primary-btn" onClick={() => inputRef.current?.click()} disabled={uploading || deleting}>
          {uploading ? 'Subiendo…' : path ? 'Reemplazar' : 'Subir imagen'}
        </button>
        {path && (
          <button type="button" className="danger-btn" onClick={handleDelete} disabled={uploading || deleting}>
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </button>
        )}
      </div>
    </div>
  )
}
